import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetDataCard() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = async () => {
    if (confirm !== 'RESET') {
      toast.error('Type RESET (all caps) to confirm');
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API_URL}/api/settings/reset-data`,
        { password, confirm }, { withCredentials: true });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`System reset — ${total} records removed`);
      setOpen(false); setPassword(''); setConfirm('');
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Card className="bg-red-950/20 border-red-900" data-testid="danger-zone">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <h2 className="text-lg font-semibold text-white">Danger Zone</h2>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Reset removes all customers, inventory, installments, payments, finance, audit &amp; product history.
          Users, branches and settings are preserved. This action is irreversible.
        </p>
      </CardHeader>
      <CardContent>
        <Button variant="destructive" onClick={() => setOpen(true)} data-testid="open-reset-dialog"
          className="bg-red-600 hover:bg-red-700 text-white">
          <Trash2 className="h-4 w-4 mr-2" /> Reset All Transactional Data
        </Button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-slate-900 border-red-900 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-400 flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5" /><span>Confirm System Reset</span>
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                This will permanently delete every customer, product, installment, payment, finance entry, audit log and product history. Users, branches and company settings are kept.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-slate-300">Your password</Label>
                <Input type="password" value={password}
                  data-testid="reset-password-input"
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Type <span className="text-red-400 font-mono">RESET</span> to confirm</Label>
                <Input value={confirm}
                  data-testid="reset-confirm-input"
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white font-mono" />
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-300">Cancel</Button>
                <Button variant="destructive" disabled={submitting} onClick={reset}
                  data-testid="confirm-reset-button"
                  className="bg-red-600 hover:bg-red-700">
                  {submitting ? 'Resetting...' : 'Reset Everything'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
